<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import TemplatesAPI from 'dashboard/api/messageTemplates';
import Button from 'dashboard/components-next/button/Button.vue';
import ChannelPhonePreview from 'dashboard/components-next/message/preview/ChannelPhonePreview.vue';
import en from './en.json';
import ru from './ru.json';

const props = defineProps({
  modelValue: { type: Number, default: null },
  templates: { type: Array, required: true },
  channel: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue', 'refresh']);
const { t } = useI18n({ useScope: 'local', messages: { en, ru } });
const router = useRouter();
const busy = ref(false);
const search = ref('');
const error = ref('');
const selected = computed(() =>
  props.templates.find(item => item.id === props.modelValue)
);
const variant = computed(
  () =>
    selected.value?.channel_variants?.[props.channel] ||
    (selected.value?.channel === props.channel ? selected.value : null)
);
const choices = computed(() =>
  props.templates.filter(
    item =>
      item.id === props.modelValue ||
      item.name.toLocaleLowerCase().includes(search.value.toLocaleLowerCase())
  )
);
const phonePreview = computed(() =>
  variant.value?.body ? { ...variant.value, text: variant.value.body } : null
);
const refresh = async () => {
  busy.value = true;
  error.value = '';
  try {
    const { data } = await TemplatesAPI.get();
    emit('refresh', data);
  } catch {
    error.value = t('templatesLoadFailed');
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <div class="mb-4">
    <input
      v-if="templates.length > 6"
      v-model="search"
      type="search"
      class="w-full text-sm"
      :aria-label="t('editor.searchTemplates')"
      :placeholder="t('editor.searchTemplates')"
    />
    <label class="block text-xs"
      >{{ t('template') }}
      <select
        :value="modelValue"
        class="w-full mt-1 text-sm"
        @change="
          emit(
            'update:modelValue',
            $event.target.value ? Number($event.target.value) : null
          )
        "
      >
        <option value="">{{ t('choose') }}</option>
        <option v-if="modelValue && !selected" :value="modelValue" disabled>
          {{ t('templateMissing') }}
        </option>
        <option v-for="item in choices" :key="item.id" :value="item.id">
          {{ item.name }}
        </option>
      </select>
    </label>
    <p v-if="!templates.length" class="text-xs leading-5 text-n-slate-11">
      {{ t('templatesEmpty') }}
    </p>
    <template v-if="selected && channel">
      <details v-if="variant?.body" open class="mt-4">
        <summary class="text-xs font-medium cursor-pointer">
          {{ t('editor.messagePreview') }}
        </summary>
        <p class="mt-2 text-xs text-n-slate-11">
          {{ t('editor.rawTemplateHint') }}
        </p>
        <ChannelPhonePreview
          v-if="['telegram', 'max', 'whatsapp', 'sms'].includes(channel)"
          :channel="channel"
          :preview="phonePreview"
          :missing-message="t('noTemplate')"
        />
        <p v-else class="text-xs whitespace-pre-wrap">{{ variant.body }}</p>
      </details>
      <p v-else role="alert" class="mt-2 text-xs text-n-ruby-11">
        {{ t('template_channel_missing') }}
      </p>
    </template>
    <div class="flex flex-wrap items-center gap-2 mt-2">
      <Button
        :label="t('refreshTemplates')"
        size="sm"
        variant="ghost"
        :disabled="busy"
        @click="refresh"
      />
      <a
        :href="
          router.resolve({
            name: 'automation_templates_index',
            query: selected ? { template_id: selected.id } : {},
          }).href
        "
        target="_blank"
        rel="noopener noreferrer"
        class="text-xs underline"
        >{{ t('allTemplateSettings') }}</a
      >
    </div>
    <p v-if="error" role="alert" class="mt-2 text-xs text-n-ruby-11">
      {{ error }}
    </p>
  </div>
</template>
